import React, { FC } from "react";
import type { HomeWhatYouCanDoProps } from "@/types";
import { ContentManager } from "../content-manager";
import { Cta } from "../cta";
import { useRef } from "react";
import { Image } from "../image";
import { useInView } from "@/hooks/reveal-in-view";
import SplitTextAnimation from "../split-text-animation";

export const HomeWhatYouCanDo: FC<HomeWhatYouCanDoProps> = ({
  title,
  cards,
  values,
  cta,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  const animateNumber = (element: Element, targetValue: number) => {
    let startValue = 0;
    const step = Math.ceil(targetValue / 100); // Increment step for smooth animation
    const duration = 1000; // Animation duration in milliseconds

    const animateValue = () => {
      if (startValue < targetValue) {
        startValue += step;
        const newText = element.innerHTML.replace(
          /\d+/,
          (startValue > targetValue ? targetValue : startValue).toString()
        );
        element.innerHTML = newText;
        requestAnimationFrame(animateValue);
      }
    };

    animateValue();
  };

  const animateNumbersInViewport = () => {
    const parentElement = document.querySelector(".homeWhatYouCanDo--values");
    if (!parentElement) return; // Check if the parent element exists

    const elements = parentElement.querySelectorAll(
      ".homeWhatYouCanDo--value-description"
    );

    const observerCallback: IntersectionObserverCallback = (
      entries,
      observer
    ) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          elements.forEach((element) => {
            const text = element.textContent;
            if (!text) return; // Check if the element's text content exists

            const regex = /\d+/g;
            const matches = text.match(regex);

            if (matches) {
              const targetValue = parseInt(matches[0], 10); // Parse the matched number as an integer
              animateNumber(element, targetValue);
            }
          });

          observer.unobserve(parentElement); // Stop observing once the parent element is in the viewport
        }
      });
    };

    const observerOptions: IntersectionObserverInit = {
      root: null, // Use the viewport as the root element
      threshold: 0.5, // Percentage of the element's visibility required to trigger the callback
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );
    observer.observe(parentElement);
  };

  // Call the animation function when the component is mounted or whenever necessary
  React.useEffect(() => {
    animateNumbersInViewport();
  }, []);

  return (
    <section className="homeWhatYouCanDo">
      <div className="wrapper">
        <SplitTextAnimation
          htmlElement={<h2 className="homeWhatYouCanDo--title">{title}</h2>}
          isInView={isInView}
        />
      </div>
      <div ref={ref} className="wrapper wrapper_lg">
        <div className="homeWhatYouCanDo--cards">
          {cards.map((card, index) => {
            return (
              <div
                style={{
                  transform: isInView
                    ? "none"
                    : `translateY(${20 * (index + 1)}px)`,
                  opacity: isInView ? 1 : 0,
                  transition: "all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)",
                }}
                key={index}
                className="homeWhatYouCanDo--card"
                onMouseEnter={(event) => {
                  const target = event.currentTarget as HTMLElement;
                  target.classList.add("hover-animation-bounce");
                }}
                onAnimationEnd={(event) => {
                  const target = event.target as HTMLElement;
                  target.classList.remove("hover-animation-bounce");
                }}
              >
                {card.image?.file && (
                  <Image
                    className="homeWhatYouCanDo--card-icon"
                    noInline
                    src={card.image as any}
                    alt={card.image.title}
                    style={{
                      transform: isInView
                        ? "none"
                        : `translateY(${40 * (index + 1)}px)`,
                      opacity: isInView ? 1 : 0,
                      transition: "all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)",
                    }}
                  />
                )}
                {card.title && (
                  <ContentManager
                    className="homeWhatYouCanDo--card-title"
                    content={card.title}
                    style={{
                      transform: isInView
                        ? "none"
                        : `translateY(${50 * (index + 1)}px)`,
                      opacity: isInView ? 1 : 0,
                      transition: "all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)",
                    }}
                  />
                )}
                {card.description && (
                  <ContentManager
                    className="homeWhatYouCanDo--card-description"
                    content={card.description}
                    style={{
                      transform: isInView
                        ? "none"
                        : `translateY(${60 * (index + 1)}px)`,
                      opacity: isInView ? 1 : 0,
                      transition: "all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="wrapper">
        <div className="homeWhatYouCanDo--values">
          {values.map((value, index) => {
            return (
              <div key={index} className="homeWhatYouCanDo--value">
                {value.title && (
                  <ContentManager
                    content={value.title}
                    className="homeWhatYouCanDo--value-title"
                  />
                )}
                {value.description && (
                  <ContentManager
                    content={value.description}
                    className="homeWhatYouCanDo--value-description"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* TODO: Temporary hidden */}
      {/* <div className="wrapper wrapper_lg">
        <Cta cta={cta} />
      </div> */}
    </section>
  );
};
